// copied from VPC.

import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import * as _ from 'underscore';
import { IError, IPodRedirectionError, IResponse, IResult } from '../../models/httpClient/IResponse';

export enum ErrorReason {
  EmptyResponse = 1000005,
  RedirectToOtherPod = 55,
  Unknown = 1000004,
}

function shouldRedirectToOtherPod(errorCode: number, destinationPodUrl: string): boolean {
  return !!errorCode && errorCode === ErrorReason.RedirectToOtherPod && destinationPodUrl !== null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getFirstErrorFromResponse(response: AxiosResponse<IResponse<any>>): IError {
  return response && response.data && response.data.result && response.data.result.errors && response.data.result.errors[0];
}

function getCodeFromError(error: IError): number {
  return error && error.code;
}

function getDestinationPodFromError(error: IError): string | undefined {
  const podRedirectionError = <IPodRedirectionError>error;
  return podRedirectionError && podRedirectionError.destinationPodUrl;
}

function makeAxiosRequest<TResult extends IResult>(requestConfig: AxiosRequestConfig): Promise<TResult | null | undefined> {
  return Axios(requestConfig).then(
    (successResponse: AxiosResponse<IResponse<TResult>>) => {
      const error = getFirstErrorFromResponse(successResponse);
      const code: number = getCodeFromError(error);
      const location = getDestinationPodFromError(error);
      if (location && shouldRedirectToOtherPod(code, location)) {
        return Promise.reject(error);
      }
      return successResponse.data && !_.isUndefined(successResponse.data.result) ? successResponse.data.result : null;
    },
    (errorObject: AxiosError<IResponse<TResult>>) => {
      // TFSID 1067441: When refreshing the page with requests still pending, the pending requests are aborted
      // by some browsers (Safari in particular). When Safari aborts these pending requests, it triggers the
      // 'error' status for the XHR, which triggers this code. The response for the aborted requests has empty
      // headers and an empty body. So, when a request is aborted in this manner, return the "EmptyResponse"
      // so this case can be handled.
      if (errorObject.response && _.isEmpty(errorObject.response.headers)) {
        return Promise.reject(ErrorReason.EmptyResponse);
      }
      const reason: number = errorObject.response ? getCodeFromError(getFirstErrorFromResponse(errorObject.response)) : ErrorReason.Unknown;
      if (errorObject.response && errorObject.response.status === 429) {
        return errorObject.response.data.result;
      }
      return Promise.reject(reason);
    }
  );
}

export default {
  makeAxiosRequest,
};
